import React from "react";

import { faTrashAlt } from "@fortawesome/free-regular-svg-icons";
import { faMinus, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Image,
  Row,
} from "react-bootstrap";
import { NavLink } from "react-router-dom";

import { displayItemTotal } from "@/utils/Helper";
import { getCurrentLang, getUrlLangPrefix } from "@/utils/Local";
import { formatArea, formatMoney, formatWeight } from "@/utils/Toolbox";

import FaCart from "../assets/icons/faCart.svg";
import DiscountComponent from "../components/Discount.component";
import DocumentComponent from "../components/Document.component";
import useCart from "../hooks/useCart";
import i18n from "../i18n";

import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import type { ChangeEvent } from "react";

import type { ICartItem, ICartItemAttribute } from "@/interfaces/ICart";

const Cart = () => {
  const { t } = i18n;
  const langPrefix = getUrlLangPrefix();
  const lang = getCurrentLang();
  const {
    dispatch,
    REDUCER_ACTIONS,
    cart,
    totalItems,
    total,
    subtotal,
    discount,
  } = useCart();

  const handleQuantityChange = (
    e: ChangeEvent<HTMLInputElement>,
    item: ICartItem,
  ) => {
    let value = Number(e.target.value);
    if (value < 1) {
      value = 1;
    } else if (value > 1000) {
      value = 999;
    }

    dispatch({
      type: REDUCER_ACTIONS.QUANTITY,
      payload: { ...item, quantity: value },
    });
  };

  return (
    <React.Fragment>
      <DocumentComponent title={t("cart")} />

      <Container className="cart-container mt-4 mb-4">
        {cart && cart.items.length ? (
          <React.Fragment>
            <Row className="mt-5 mb-5">
              <Col>
                <div className="d-flex flex-column flex-lg-row justify-content-between align-items-center">
                  <h1>
                    {t("shopping_cart")}{" "}
                    <span className="text-uppercase ms-2">
                      {`${totalItems}  ${totalItems > 1 ? t("items") : t("item")}`}{" "}
                    </span>
                  </h1>

                  <DiscountComponent />
                </div>
              </Col>
            </Row>

            <Row className="mt-5 mb-5">
              <Col lg={9} xl={8}>
                <Row className="border-bottom cart-list-header">
                  <Col xs={6}>{t("item")}</Col>
                  <Col className="ps-4 d-none d-lg-block" xs={2}>
                    {t("price")}
                  </Col>
                  <Col className="text-start d-none d-lg-block" xs={2}>
                    {t("quantity")}
                  </Col>
                  <Col className="text-end" lg={1} xs={6}>
                    {t("total")}
                  </Col>
                  <Col lg={1} />
                </Row>

                {cart.items.map((item: ICartItem) => (
                  <Row
                    key={item.sku}
                    className="border-bottom pt-4 pb-4 cart-list-body"
                  >
                    {/* Mobile Screen */}
                    <Col className="d-block d-lg-none">
                      <Row>
                        <Col xs={2}>
                          <NavLink
                            className="d-grid gap-2"
                            to={`${langPrefix}/${t("_route.shop")}/${item.slug}`}
                          >
                            <Image
                              rounded
                              className="mw-100 m-auto mt-0 mb-0"
                              role="button"
                              src={item.image}
                            />
                          </NavLink>
                        </Col>

                        <Col xs={7}>
                          <Row>
                            <Col className="cart-item cart-item-info" xs={10}>
                              <NavLink
                                className="d-grid gap-2 fw-bold"
                                to={`${langPrefix}/${t("_route.shop")}/${item.slug}`}
                              >
                                {item.name}
                              </NavLink>
                              {item.sku}
                            </Col>
                          </Row>

                          <Row>
                            <Col xs={12}>
                              {item.attributes?.map(
                                (attribute: ICartItemAttribute) => (
                                  <p key={attribute.option.id} className="m-0">
                                    {`${attribute.option.label[lang].trim()}: ${attribute.option_value.label[lang]}`}
                                  </p>
                                ),
                              )}

                              <p className="m-0">
                                {`${t("total_area")}: ${formatArea(item.area * item.quantity, lang)}`}
                              </p>

                              <p className="m-0">
                                {`${t("total_weight")}: ${formatWeight(item.weight * item.quantity)}`}
                              </p>
                            </Col>
                          </Row>

                          <Row className="mb-3">
                            <Col
                              className="cart-item cart-item-price pt-1"
                              xs={{ span: 12 }}
                            >
                              <p>{`${t("price_per_panel")}: ${formatMoney(item.list / 100)}`}</p>
                            </Col>
                          </Row>

                          <Row>
                            <Col
                              className="cart-item cart-item-quantity"
                              xs={{ span: 8 }}
                            >
                              <div className="d-flex align-items-center justify-content-between border rounded p-1 ps-2 pe-2">
                                <FontAwesomeIcon
                                  icon={faMinus}
                                  onClick={() =>
                                    item.quantity > 1
                                      ? dispatch({
                                          type: REDUCER_ACTIONS.QUANTITY,
                                          payload: {
                                            ...item,
                                            quantity: item.quantity - 1,
                                          },
                                        })
                                      : undefined
                                  }
                                  style={{
                                    cursor:
                                      item.quantity > 1
                                        ? "pointer"
                                        : "not-allowed",
                                  }}
                                />
                                <Form.Control
                                  required
                                  className="quantity-input"
                                  maxLength={10}
                                  type="number"
                                  value={item.quantity}
                                  onChange={(
                                    e: ChangeEvent<HTMLInputElement>,
                                  ) => handleQuantityChange(e, item)}
                                />
                                <FontAwesomeIcon
                                  icon={faPlus}
                                  onClick={() =>
                                    item.quantity < 999
                                      ? dispatch({
                                          type: REDUCER_ACTIONS.QUANTITY,
                                          payload: {
                                            ...item,
                                            quantity: item.quantity + 1,
                                          },
                                        })
                                      : undefined
                                  }
                                  style={{
                                    cursor:
                                      item.quantity < 999
                                        ? "pointer"
                                        : "not-allowed",
                                  }}
                                />
                              </div>
                            </Col>
                          </Row>
                        </Col>

                        <Col
                          className="cart-item cart-item-total text-end pt-1"
                          xs={3}
                        >
                          {displayItemTotal(item)}
                          <Button
                            className="text-start text-decoration-none p-0 cart-item-remove-btn"
                            variant="link"
                            onClick={() =>
                              dispatch({
                                type: REDUCER_ACTIONS.REMOVE,
                                payload: item,
                              })
                            }
                          >
                            <FontAwesomeIcon icon={faTrashAlt as IconProp} />
                          </Button>
                        </Col>
                      </Row>
                    </Col>

                    {/* Large Screen */}
                    <Col className="d-none d-lg-block">
                      <Row>
                        <Col xs={2}>
                          <NavLink
                            className="d-grid gap-2"
                            to={`${langPrefix}/${t("_route.shop")}/${item.slug}`}
                          >
                            <Image
                              rounded
                              className="mw-100 m-auto mt-0 mb-0"
                              role="button"
                              src={item.image}
                            />
                          </NavLink>
                        </Col>

                        <Col xs={10}>
                          <Row>
                            <Col className="cart-item cart-item-info" xs={5}>
                              <NavLink
                                className="d-grid gap-2 fw-bold"
                                to={`${langPrefix}/${t("_route.shop")}/${item.slug}`}
                              >
                                {item.name}
                              </NavLink>
                              {item.sku}
                            </Col>

                            <Col
                              className="cart-item cart-item-price pt-1"
                              xs={2}
                            >
                              <span>{formatMoney(item.list / 100)}</span>
                            </Col>

                            <Col
                              className="cart-item cart-item-quantity"
                              xs={2}
                            >
                              <div className="d-flex align-items-center justify-content-between border rounded p-1 ps-2 pe-2">
                                <FontAwesomeIcon
                                  icon={faMinus}
                                  onClick={() =>
                                    item.quantity > 1
                                      ? dispatch({
                                          type: REDUCER_ACTIONS.QUANTITY,
                                          payload: {
                                            ...item,
                                            quantity: item.quantity - 1,
                                          },
                                        })
                                      : undefined
                                  }
                                  style={{
                                    cursor:
                                      item.quantity > 1
                                        ? "pointer"
                                        : "not-allowed",
                                  }}
                                />
                                <Form.Control
                                  required
                                  className="quantity-input"
                                  maxLength={10}
                                  type="number"
                                  value={item.quantity}
                                  onChange={(
                                    e: ChangeEvent<HTMLInputElement>,
                                  ) => handleQuantityChange(e, item)}
                                />
                                <FontAwesomeIcon
                                  icon={faPlus}
                                  onClick={() =>
                                    item.quantity < 999
                                      ? dispatch({
                                          type: REDUCER_ACTIONS.QUANTITY,
                                          payload: {
                                            ...item,
                                            quantity: item.quantity + 1,
                                          },
                                        })
                                      : undefined
                                  }
                                  style={{
                                    cursor:
                                      item.quantity < 999
                                        ? "pointer"
                                        : "not-allowed",
                                  }}
                                />
                              </div>
                            </Col>

                            <Col
                              className="cart-item cart-item-total text-end pt-1"
                              xs={2}
                            >
                              {displayItemTotal(item)}
                            </Col>

                            <Col className="text-end pt-1" xs={1}>
                              <Button
                                className="text-start text-decoration-none p-0 cart-item-remove-btn"
                                variant="link"
                                onClick={() =>
                                  dispatch({
                                    type: REDUCER_ACTIONS.REMOVE,
                                    payload: item,
                                  })
                                }
                              >
                                <FontAwesomeIcon
                                  icon={faTrashAlt as IconProp}
                                />
                              </Button>
                            </Col>
                          </Row>

                          <Row>
                            <Col xs={11}>
                              {item.attributes?.map(
                                (attribute: ICartItemAttribute) => (
                                  <p
                                    key={attribute.option_value.id}
                                    className="m-0"
                                  >
                                    {`${attribute.option.label[lang].trim()}: ${attribute.option_value.label[lang]}`}
                                  </p>
                                ),
                              )}

                              <p className="m-0">
                                {`${t("total_area")}: ${formatArea(item.area * item.quantity, lang)}`}
                              </p>

                              <p className="m-0">
                                {`${t("total_weight")}: ${formatWeight(item.weight * item.quantity)}`}
                              </p>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                ))}
              </Col>

              <Col lg={3} xl={{ span: 3, offset: 1 }}>
                <Card>
                  <Card.Body className="p-4">
                    <Card.Title className="mb-4">
                      {t("order_summary")}
                    </Card.Title>

                    <div className="d-flex justify-content-between mb-3">
                      <span>{t("sub_total")}</span>
                      <span>{formatMoney(subtotal / 100)}</span>
                    </div>

                    {discount > 0 && (
                      <div className="d-flex justify-content-between mb-3">
                        <span>{t("discount")}</span>
                        <span className="text-danger">
                          -{formatMoney(discount / 100)}
                        </span>
                      </div>
                    )}

                    <div className="d-flex justify-content-between mb-3">
                      <span>{t("estimated_taxes")}</span>
                      <span>{formatMoney(0)}</span>
                    </div>

                    <hr style={{ margin: "1rem 0" }} />

                    <div className="d-flex justify-content-between mb-3 fw-bold">
                      <span>{t("estimated_total")}</span>
                      <span>{formatMoney(total / 100)}</span>
                    </div>

                    <div className="d-grid gap-2 mt-4">
                      <NavLink
                        className="d-grid gap-2"
                        to={`${langPrefix}/${t("_route.checkout")}`}
                      >
                        <Button variant="primary">
                          {t("proceed_to_checkout")}
                        </Button>
                      </NavLink>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Row className="mt-5 mb-2">
              <Col>
                <h1>{t("shopping_cart")}</h1>
              </Col>
            </Row>

            <Row className="">
              <Col xs={12}>
                <p>{t("empty_shopping_cart")}</p>
              </Col>

              <Col xs={12}>
                <NavLink
                  className="mt-2"
                  to={`${langPrefix}/${t("_route.shop")}`}
                >
                  <Button variant="outline-primary d-flex justify-content-center gap-2 ps-4 pe-4 pt-2 pb-2">
                    <img alt={t(`continue_shopping`)} src={FaCart} />
                    <span>{t("continue_shopping")}</span>
                  </Button>
                </NavLink>
              </Col>
            </Row>
          </React.Fragment>
        )}
      </Container>
    </React.Fragment>
  );
};

export default Cart;
